import localforage from 'localforage';

import type { BookMeta, Group } from '../types/book';

const DRIVE_SYNC_STATE_KEY = 'googleDriveSyncState';

localforage.config({
  name: 'WeReader',
  storeName: 'ePubData',
});

export type DriveDeletedBook = {
  id: string;
  title?: string;
  hash?: string;
  deletedAt: number;
  fileId?: string;
  coverFileId?: string;
  notesFileId?: string;
};

export type DriveDeletedGroup = {
  id: string;
  name?: string;
  parentId?: string;
  updatedAt?: number;
  deletedAt: number;
};

export type LocalDriveSyncState = {
  deviceId: string;
  lastSyncAt?: number;
  lastError?: string | null;
  deletedBooks: Record<string, DriveDeletedBook>;
  deletedGroups: Record<string, DriveDeletedGroup>;
};

const createDeviceId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getLocalDriveSyncState = async (): Promise<LocalDriveSyncState> => {
  const state = (await localforage.getItem<LocalDriveSyncState>(DRIVE_SYNC_STATE_KEY)) || null;
  if (state?.deviceId) {
    return {
      ...state,
      deletedBooks: state.deletedBooks || {},
      deletedGroups: state.deletedGroups || {},
    };
  }

  const initialized: LocalDriveSyncState = {
    deviceId: createDeviceId(),
    deletedBooks: {},
    deletedGroups: {},
  };
  await localforage.setItem(DRIVE_SYNC_STATE_KEY, initialized);
  return initialized;
};

export const updateLocalDriveSyncState = async (
  update: Partial<LocalDriveSyncState> | ((state: LocalDriveSyncState) => LocalDriveSyncState),
): Promise<LocalDriveSyncState> => {
  const current = await getLocalDriveSyncState();
  const next = typeof update === 'function' ? update(current) : { ...current, ...update };
  next.deletedBooks = next.deletedBooks || {};
  next.deletedGroups = next.deletedGroups || {};
  await localforage.setItem(DRIVE_SYNC_STATE_KEY, next);
  return next;
};

export const recordLocalDriveBookDeletion = async (book: BookMeta): Promise<void> => {
  const drive = book.cloud?.googleDrive;
  const deletedAt = Date.now();
  await updateLocalDriveSyncState((state) => ({
    ...state,
    deletedBooks: {
      ...state.deletedBooks,
      [book.id]: {
        id: book.id,
        title: book.title,
        hash: book.hash,
        deletedAt,
        fileId: drive?.fileId,
        coverFileId: drive?.coverFileId,
        notesFileId: drive?.notesFileId,
      },
    },
  }));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('wereader:local-data-changed', {
        detail: {
          reason: 'book-deleted',
          bookId: book.id,
        },
      }),
    );
  }
};

export const recordLocalDriveGroupDeletion = async (group: Group): Promise<void> => {
  const deletedAt = Date.now();
  await updateLocalDriveSyncState((state) => ({
    ...state,
    deletedGroups: {
      ...state.deletedGroups,
      [group.id]: {
        id: group.id,
        name: group.name,
        parentId: group.parentId,
        updatedAt: group.updatedAt || group.createdAt,
        deletedAt,
      },
    },
  }));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('wereader:local-data-changed', {
        detail: {
          reason: 'group-deleted',
          groupId: group.id,
        },
      }),
    );
  }
};
